import { FC, useCallback, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { toast } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle, faPlusCircle } from '@fortawesome/free-solid-svg-icons'
import { ReactComponent as ReportsIcon } from 'src/assets/svg/reports.svg'
import { Skeleton } from 'src/components/Skeleton'
import {
  useDeleteCustomReportMutation,
  useRetryCustomReportMutation,
} from 'src/reactQuery/useCustomReportMutations'
import { useGetCustomReportsQuery } from 'src/reactQuery/useGetCustomReportsQuery'
import { CustomReportsApiService } from 'src/services/customReportsApi'
import { GenerateReportModal } from './GenerateReportModal'
import { ModuleFilterDropdown } from './ModuleFilterDropdown'
import { Report, ReportModule, toReport } from './interfaces/report.interface'
import { ReportCard } from './ReportCard'
import styles from './yourReports.module.scss'

// Both glyphs are inlined to match the Campaigns toolbar, which draws them as
// inline SVG rather than importing an asset.
const SearchGlyph: FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    fill="none"
    height="20"
    viewBox="0 0 24 24"
    width="20"
  >
    <path
      d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
)

const FilterGlyph: FC = () => (
  <svg fill="none" height="20" viewBox="0 0 24 24" width="20">
    <path
      d="M3 6H21M7 12H17M10 18H14"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="2"
    />
  </svg>
)

// .reportCard is 117px min-height / radius 18 (reportCard.module.scss).
const REPORT_CARD_HEIGHT = 117
const REPORT_CARD_RADIUS = 18
const REPORT_SKELETON_COUNT = 3

interface YourReportsProps {
  // 'widget' — standalone card (the HomePage). 'page' — dropped into a card the
  // page already owns (Custom Reporting), so it drops its own background,
  // padding and radius rather than nesting a white card inside a white card.
  // Mirrors ProjectsWidget's variant prop.
  variant?: 'page' | 'widget'
}

// The container deliberately has no fixed height: the spec's 695px is the
// height *with* a full list in it, and the widget shrinks to fit fewer cards.
export const YourReports: FC<YourReportsProps> = ({ variant = 'widget' }) => {
  const { data, isLoading } = useGetCustomReportsQuery()
  const retryMutation = useRetryCustomReportMutation()
  const deleteMutation = useDeleteCustomReportMutation()

  // Ids currently being downloaded. The backend has no 'downloading' status —
  // it is a client-side concern — so it is tracked here and merged onto the
  // card below.
  const [downloadingIds, setDownloadingIds] = useState<string[]>([])

  const allItems: Report[] = (data?.data || []).map((report) => {
    const mapped = toReport(report)

    return downloadingIds.includes(mapped.id)
      ? { ...mapped, status: 'downloading' }
      : mapped
  })

  const handleDownload = async (report: Report) => {
    setDownloadingIds((prev) => [...prev, report.id])

    try {
      await CustomReportsApiService.download(report.id, `${report.name}.pdf`)
    } catch {
      toast.error('Failed to download report')
    } finally {
      setDownloadingIds((prev) => prev.filter((id) => id !== report.id))
    }
  }

  const [searchValue, setSearchValue] = useState('')
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  // Handed to the dropdown so its outside-click handler can ignore the trigger.
  // Without it, mousedown closed the panel and the following click reopened it,
  // so the button appeared not to toggle.
  const filterTriggerRef = useRef<HTMLButtonElement>(null)
  // Two selections, deliberately: `draft` follows every tick so the Filters
  // pill and its count react immediately, while `selectedModules` only moves on
  // Apply — so the list underneath does not shift while the user is still
  // choosing.
  const [draftModules, setDraftModules] = useState<ReportModule[]>([])
  const [selectedModules, setSelectedModules] = useState<ReportModule[]>([])

  const normalizedSearch = searchValue.trim().toLowerCase()

  // A report matches the module filter if it was built from ANY checked module
  // — checking two modules widens the results rather than requiring both.
  const items = allItems.filter((report) => {
    const matchesSearch =
      !normalizedSearch || report.name.toLowerCase().includes(normalizedSearch)
    const matchesModules =
      !selectedModules.length ||
      report.modules.some((module) => selectedModules.includes(module))

    return matchesSearch && matchesModules
  })

  // Which edges have content scrolled past them. Drives the fade masks so an
  // edge only softens when there is genuinely more list in that direction.
  const listRef = useRef<HTMLDivElement>(null)
  const [hasScrollAbove, setHasScrollAbove] = useState(false)
  const [hasScrollBelow, setHasScrollBelow] = useState(false)

  const syncFades = useCallback(() => {
    const el = listRef.current
    if (!el) return

    const { clientHeight, scrollHeight, scrollTop } = el
    setHasScrollAbove(scrollTop > 1)
    // -1 absorbs sub-pixel rounding at the very bottom.
    setHasScrollBelow(scrollTop + clientHeight < scrollHeight - 1)
  }, [])

  // Re-run when the list changes: a shorter list may not scroll at all.
  useEffect(() => {
    syncFades()
  }, [items.length, syncFades])

  const fadeClass = classNames({
    [styles.fadeBoth]: hasScrollAbove && hasScrollBelow,
    [styles.fadeBottom]: !hasScrollAbove && hasScrollBelow,
    [styles.fadeTop]: hasScrollAbove && !hasScrollBelow,
  })

  // Counts everything the user owns, not the filtered view — the badge reads as
  // "you have N reports", so showing 0 while a filter hid them was a false
  // claim. 0 → "reports", 1 → "1 report", 2+ → "reports".
  const countLabel = `${allItems.length} ${
    allItems.length === 1 ? 'report' : 'reports'
  }`

  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false)

  const generateModal = (
    <GenerateReportModal
      isOpen={isGenerateModalOpen}
      onClose={() => setIsGenerateModalOpen(false)}
    />
  )

  const emptyStateText =
    "You haven't generated any reports yet. Create your first PDF report to " +
    'capture a snapshot of your analytics, insights, and campaign performance.'

  // On the HomePage the widget keeps its header (minus the search, filters and
  // count, which have nothing to act on) and lays the empty state out
  // horizontally, so the card stays compact at 202px rather than reserving the
  // page's full-height centred treatment.
  // Keyed on allItems, not the filtered list: "you have no reports" and "your
  // filter matched nothing" are different claims, and only the first belongs in
  // this empty state.
  if (!isLoading && !allItems.length && variant === 'widget') {
    return (
      <div className={styles.yourReports}>
        <div className={styles.headerBlock}>
          <div className={styles.headerRow}>
            <div className={styles.headerText}>
              <h3 className={styles.title}>Your Reports</h3>

              <div className={styles.subtitleRow}>
                <FontAwesomeIcon
                  className={styles.infoIcon}
                  icon={faInfoCircle}
                />
                <span className={styles.subtitle}>
                  Create downloadable PDF reports that summarize your analytics,
                  insights, and campaign performance over any selected time
                  period.
                </span>
              </div>
            </div>

            <button
              className={styles.generateButton}
              onClick={() => setIsGenerateModalOpen(true)}
              type="button"
            >
              <FontAwesomeIcon
                className={styles.generateButtonIcon}
                icon={faPlusCircle}
              />
              Generate Full Report
            </button>
          </div>

          <hr className={styles.divider} />
        </div>

        <div className={styles.emptyStateRow}>
          <span className={styles.emptyStateIconBox}>
            <ReportsIcon className={styles.emptyStateIcon} />
          </span>

          <div className={styles.emptyStateRowText}>
            <h3 className={styles.emptyStateRowTitle}>No Reports Yet</h3>
            <p className={styles.emptyStateRowSubtext}>{emptyStateText}</p>
          </div>
        </div>

        {generateModal}
      </div>
    )
  }

  // On a page the header collapses to just the rule and the CTA moves into the
  // centred empty state, which fills the host card's fixed height. Gated on
  // !isLoading so an in-flight query never claims the user has no reports.
  if (!isLoading && !allItems.length) {
    return (
      <div
        className={classNames(
          styles.yourReports,
          styles.fillHost,
          styles.plain
        )}
      >
        <hr className={styles.divider} />

        <div className={styles.emptyState}>
          <span className={styles.emptyStateIconBox}>
            <ReportsIcon className={styles.emptyStateIcon} />
          </span>

          <h3 className={styles.emptyStateTitle}>No Reports Yet</h3>

          <p className={styles.emptyStateText}>{emptyStateText}</p>

          <button
            className={styles.generateButton}
            onClick={() => setIsGenerateModalOpen(true)}
            type="button"
          >
            <FontAwesomeIcon
              className={styles.generateButtonIcon}
              icon={faPlusCircle}
            />
            Generate Full Report
          </button>
        </div>

        {generateModal}
      </div>
    )
  }

  return (
    <div
      className={classNames(styles.yourReports, {
        [styles.plain]: variant === 'page',
      })}
    >
      <div className={styles.headerBlock}>
        <div className={styles.headerRow}>
          <div className={styles.headerText}>
            <h3 className={styles.title}>Your Reports</h3>

            <div className={styles.subtitleRow}>
              <FontAwesomeIcon
                className={styles.infoIcon}
                icon={faInfoCircle}
              />
              <span className={styles.subtitle}>
                Create downloadable PDF reports that summarize your analytics,
                insights, and campaign performance over any selected time
                period.
              </span>
            </div>
          </div>

          <span className={styles.countBadge}>
            <ReportsIcon className={styles.countIcon} />
            <span className={styles.countLabel}>{countLabel}</span>
          </span>
        </div>

        <div className={styles.toolbar}>
          <div className={styles.toolbarLeft}>
            <div className={styles.searchBox}>
              <SearchGlyph className={styles.searchIcon} />
              <input
                className={styles.searchInput}
                onChange={(event) => setSearchValue(event.target.value)}
                placeholder="Search"
                type="text"
                value={searchValue}
              />
            </div>

            {/* Mirrors the Campaigns filter control (Social Listening →
                Campaigns, in TabsSection) — same label, icon and button. That
                one is inline markup rather than a shared component, so it is
                matched here rather than imported. */}
            <div className={styles.filtersGroup}>
              {/* One stable button in both states, not two shapes swapped by
                  a ternary — React reconciles siblings by position, so
                  changing the trigger's shape on every tick could remount the
                  dropdown rendered next to it. */}
              <button
                className={classNames(styles.filterTrigger, {
                  [styles.filterTriggerActive]: !!draftModules.length,
                })}
                onClick={() => setIsFilterOpen((prev) => !prev)}
                ref={filterTriggerRef}
                type="button"
              >
                {!!draftModules.length && (
                  <span className={styles.filterPillCount}>
                    {draftModules.length}
                  </span>
                )}
                <span>Filters</span>
                <span className={styles.filterTriggerGlyph}>
                  <FilterGlyph />
                </span>
              </button>

              {isFilterOpen && (
                <ModuleFilterDropdown
                  onApply={() => {
                    setSelectedModules(draftModules)
                    setIsFilterOpen(false)
                  }}
                  onChange={setDraftModules}
                  onClose={() => {
                    // Abandoning the panel discards the draft rather than
                    // leaving the pill claiming a filter that was never applied.
                    setDraftModules(selectedModules)
                    setIsFilterOpen(false)
                  }}
                  selectedModules={draftModules}
                  triggerRef={filterTriggerRef}
                />
              )}
            </div>
          </div>

          <button
            className={styles.generateButton}
            onClick={() => setIsGenerateModalOpen(true)}
            type="button"
          >
            <FontAwesomeIcon
              className={styles.generateButtonIcon}
              icon={faPlusCircle}
            />
            Generate Full Report
          </button>
        </div>

        <hr className={styles.divider} />
      </div>

      {/* Max height, not a fixed one — a short list shrinks the widget rather
          than leaving dead space, and a long one scrolls inside the card. */}
      <div
        className={`${styles.reportsList} ${fadeClass}`}
        onScroll={syncFades}
        ref={listRef}
      >
        {isLoading ? (
          // Neither empty state is true yet. Without this the list fell through
          // to "No matching reports" on first paint — telling the user their
          // filters excluded everything before anything had loaded.
          Array.from({ length: REPORT_SKELETON_COUNT }).map((_, index) => (
            <Skeleton
              height={REPORT_CARD_HEIGHT}
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              radius={REPORT_CARD_RADIUS}
            />
          ))
        ) : items.length ? (
          items.map((report) => (
            <ReportCard
              key={report.id}
              onDelete={() => deleteMutation.mutate(report.id)}
              onDownload={() => handleDownload(report)}
              onRetry={() => retryMutation.mutate(report.id)}
              report={report}
            />
          ))
        ) : (
          // Same horizontal treatment as the empty state, so the widget keeps
          // its compact shape rather than opening into a tall centred block.
          // The copy stays distinct from "No Reports Yet": the user does have
          // reports, this filter just excludes them all.
          <div className={styles.emptyStateRow}>
            <span className={styles.emptyStateIconBox}>
              <ReportsIcon className={styles.emptyStateIcon} />
            </span>

            <div className={styles.emptyStateRowText}>
              <h3 className={styles.emptyStateRowTitle}>No matching reports</h3>
              <p className={styles.emptyStateRowSubtext}>
                Try a different search term or clear your module filters.
              </p>
            </div>
          </div>
        )}
      </div>

      {generateModal}
    </div>
  )
}
